import React, { useContext, useState } from "react";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import RadioGroup from "components/radio-group";
import StoreFormContainer from "components/store-form-container";
import SingleCheckbox from "components/checkbox";
import { getQueryParams, isDropoffStore } from "lib/utils";
import { useTranslation } from "react-i18next";
import AuthContext from "contexts/AuthContext";
import { useTakebackById } from "../../api/use-takebacks";
import { Button } from "components/button";
import ConfirmationDialog from "components/confirmation-dialog";
import { useAddStatus } from "../../api/tes/useAddStatus";
import { useUpdateTakeback } from "../../api/tes/useUpdateTakeback";
import { createVerificationData } from "../../lib/verification-utils";

const radioOptions2 = [
  {
    value: true,
    title: "Yes",
    iconstyle: "scale-125",
  },
  {
    value: false,
    title: "No",
    iconstyle: "scale-125",
  },
];

const rejectionOptions = [
  { id: "non-lego", label: "Non-LEGO bricks or elements" },
  { id: "attached", label: "Attached LEGO bricks or elements" },
  { id: "electronic", label: "Electronic components or batteries" },
  {
    id: "other-branded",
    label:
      "Other LEGO branded items such as apparel, key chains, storage containers, building instructions and packaging",
  },
  { id: "duplo", label: "LEGO® DUPLO® bricks and elements" },
];

export default function CreateTradeIn() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [conditionAcceptable, setConditionAcceptable] = useState(true);
  const [selectedRejections, setSelectedRejections] = useState({});
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const addStatus = useAddStatus();
  const updateTakeback = useUpdateTakeback();
  const takeback = useTakebackById({ id: queryParams.id });

  const handleRejectionChange = (id, checked) => {
    setSelectedRejections((prev) => ({
      ...prev,
      [id]: checked,
    }));
  };

  const handleSubmit = async () => {
    try {
      // Prepare rejected items data
      const rejectedItemsData = !conditionAcceptable
        ? {
            rejectedItems: selectedRejections,
            selectedRejections: Object.entries(selectedRejections)
              .filter(([_, v]) => v)
              .map(([k]) => k),
          }
        : {};

      // Create verification data and status
      const verificationData = createVerificationData("associate", {
        associateEmail: user.email,
      });

      // Update takeback with condition information and rejected items
      const updatedData = {
        ...takeback?.rawTakeback?.data,
        conditionAcceptable,
        ...rejectedItemsData,
        ...verificationData,
      };

      await updateTakeback.mutateAsync({
        id: queryParams.id,
        input: {
          data: updatedData,
        },
      });

      // Update takeback with verification data and add status
      await updateTakeback.mutateAsync({
        id: queryParams.id,
        input: {
          data: updatedData,
        },
      });

      await addStatus.mutateAsync({
        id: queryParams.id,
        input: {
          parent_status: "processing",
          status: conditionAcceptable ? "all-conformed" : "partially-conformed",
        },
      });

      isDropoffStore(user)
        ? navigate({
            pathname: `/en-US/store/trade-in-complete/dropoff`,
            search: createSearchParams(queryParams).toString(),
          })
        : navigate({
            pathname: t("urls.store_trade_in_complete"),
            search: createSearchParams(queryParams).toString(),
          });
    } catch (error) {
      console.error("Error submitting trade-in acceptance:", error);
      alert("Please try again");
    }
  };

  const isContinueEnabled =
    conditionAcceptable ||
    (!conditionAcceptable && Object.values(selectedRejections).some(Boolean));

  const handleCancel = () => {
    setIsDialogOpen(true);
  };

  const confirmCancel = () => {
    setIsDialogOpen(false);
    navigate(t("urls.store_check_in"), { replace: true });
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const showCancelButton = !conditionAcceptable && selectedRejections.length;

  return (
    <StoreFormContainer
      submitLabel="Continue"
      secondaryLabel="Back"
      secondaryHref={t("urls.store_check_in")}
      submitDisabled={!isContinueEnabled}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col flex-grow items-center py-4">
        <div className="flex flex-col mr-auto mb-6">
          <h1 className="text-6xl font-bold pb-3">
            {t("store.trade-in-acceptance.title")}
          </h1>
          <div className="text-tertiary text-3xl">
            Condition of the LEGO® bricks
          </div>
        </div>
        <div className="bg-white w-full py-4 ">
          <h2 className="text-secondary max-w-lg mt-xxx-large mb-regular">
            Did any items not meet the requirements?
          </h2>
          <RadioGroup
            options={radioOptions2}
            name="custom-radio"
            selectedOption={!conditionAcceptable}
            onChange={(val) => setConditionAcceptable(!val)}
            className="mt-regular"
          />
          {!conditionAcceptable && (
            <div>
              <h2 className="text-primary max-w-lg mt-xxx-large mb-regular">
                Select rejected items<span className="text-red">*</span>
              </h2>
              <div className="space-y-3 mb-6">
                {rejectionOptions.map((option) => (
                  <div
                    key={option.id}
                    className={`cursor-pointer flex border ${
                      !selectedRejections[option.id]
                        ? "border-[#d2d2d2] bg-white"
                        : "border-[#0275bf] bg-[#eff6ff]"
                    } p-4 rounded-lg focus:border-[#0275bf]`}
                  >
                    <SingleCheckbox
                      label={option.label}
                      checked={selectedRejections[option.id] || false}
                      setChecked={(checked) =>
                        handleRejectionChange(option.id, checked)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {showCancelButton && (
          <Button
            onClick={handleCancel}
            className="mt-4 px-4 py-2 rounded-full mr-auto"
          >
            Cancel
          </Button>
        )}
      </div>
      <ConfirmationDialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        onConfirm={confirmCancel}
        title="Are you sure you want to cancel and close the trade-in?"
        primaryText="Yes, cancel and close the trade-in"
        secondaryText="Do not cancel and continue"
      />
    </StoreFormContainer>
  );
}
